import { useState } from "react";
import { Check, Copy, KeyRound, Plug, Plus, ShieldOff, TriangleAlert } from "lucide-react";

import { ErrorApi } from "../api/cliente.ts";
import type { Credencial, CredencialCreada } from "../api/tipos.ts";
import { usarCredenciales, usarCrearCredencial, usarRevocarCredencial } from "../ganchos/datos.ts";
import { fechaHora, haceRato } from "../formato.ts";
import { EsqueletoTabla } from "../componentes/Errores.tsx";
import { Aviso, Boton, Campo, Dialogo, Insignia, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";

/**
 * Las credenciales con las que entra una máquina (SPEC-019 §3).
 *
 * Va fuera del formulario de configuración a propósito. Crear una credencial
 * ocurre en el momento y no es un cambio pendiente que se guarde con lo demás:
 * meterla dentro haría que el botón «Guardar» significara dos cosas distintas.
 *
 * Lo que gobierna esta pantalla es que **el secreto se enseña una vez**. De ahí
 * salen las tres decisiones que se ven: el token aparece en un diálogo que hay
 * que cerrar a conciencia, la lista no lo tiene ni puede pedirlo, y revocar pide
 * confirmación porque del otro lado hay una integración corriendo.
 */
export function Credenciales() {
  const { data, isPending, error } = usarCredenciales();
  const crear = usarCrearCredencial();
  const revocar = usarRevocarCredencial();

  const [nombre, setNombre] = useState("");
  const [reciente, setReciente] = useState<CredencialCreada | null>(null);
  const [porRevocar, setPorRevocar] = useState<Credencial | null>(null);

  const credenciales = data?.credenciales ?? [];
  const vivas = credenciales.filter((c) => !c.revocada_en);

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) return;
    crear.mutate(nombre.trim(), {
      onSuccess: (c) => {
        setReciente(c);
        setNombre("");
      },
    });
  }

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-tinta">
          <Plug className="size-5 text-marca" aria-hidden />
          Credenciales de servicio
        </h2>
        <p className="max-w-[68ch] text-sm text-tenue">
          Con esto entra un sistema tuyo —un ERP, un punto de venta— a cargar cartera y a leer lo
          conciliado. No uses la contraseña de una persona: si un proceso entra con ella, el
          registro de accesos deja de decir quién hizo qué.{" "}
          <a href="/integracion" className="font-medium text-marca hover:underline">
            Cómo se usa
          </a>
          .
        </p>
      </header>

      {error instanceof ErrorApi && error.esSinPermiso ? (
        <Aviso tono="aviso" titulo="Esto lo gestiona el dueño de la cuenta">
          Tu rol no puede ver ni crear credenciales.
        </Aviso>
      ) : (
        <>
          <Tarjeta className="p-4 sm:p-5">
            <form className="flex flex-wrap items-end gap-3" onSubmit={enviar}>
              <div className="min-w-56 flex-1">
                <Campo
                  etiqueta="Para qué es"
                  ayuda="Su nombre es lo único que la distingue después. «ERP de la tienda principal» se puede revocar sin miedo; «credencial 2» no."
                  placeholder="ERP de la tienda principal"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  error={crear.error instanceof ErrorApi ? crear.error.message : undefined}
                />
              </div>
              <Boton
                type="submit"
                variante="primario"
                icono={Plus}
                cargando={crear.isPending}
                disabled={!nombre.trim()}
              >
                Crear credencial
              </Boton>
            </form>
          </Tarjeta>

          {isPending ? (
            <Tarjeta className="p-4">
              <EsqueletoTabla filas={2} />
            </Tarjeta>
          ) : credenciales.length === 0 ? (
            <Tarjeta>
              <Vacio icono={KeyRound} titulo="Ninguna credencial todavía">
                Mientras no haya ninguna, ningún sistema externo puede entrar a esta cuenta.
              </Vacio>
            </Tarjeta>
          ) : (
            <ul className="space-y-2">
              {credenciales.map((c) => (
                <Fila
                  key={c.id}
                  credencial={c}
                  unica={vivas.length === 1 && !c.revocada_en}
                  alRevocar={() => setPorRevocar(c)}
                />
              ))}
            </ul>
          )}
        </>
      )}

      <TokenNuevo credencial={reciente} alCerrar={() => setReciente(null)} />

      <Dialogo
        abierto={!!porRevocar}
        alCerrar={() => setPorRevocar(null)}
        titulo="¿Revocar esta credencial?"
        descripcion={
          porRevocar
            ? `«${porRevocar.nombre}» dejará de funcionar en la siguiente llamada. Si hay un sistema usándola, deja de cargar y de leer en ese momento — no al rato.`
            : undefined
        }
        pie={
          <>
            <Boton variante="suave" onClick={() => setPorRevocar(null)}>
              Cancelar
            </Boton>
            <Boton
              variante="peligro"
              icono={ShieldOff}
              cargando={revocar.isPending}
              onClick={() =>
                porRevocar &&
                revocar.mutate(porRevocar.id, { onSuccess: () => setPorRevocar(null) })
              }
            >
              Revocar
            </Boton>
          </>
        }
      >
        <p className="text-sm text-tenue">
          No se puede deshacer. Para volver a conectar ese sistema hay que crear una credencial
          nueva y cambiarla allá.
        </p>
      </Dialogo>
    </section>
  );
}

function Fila({
  credencial: c,
  unica,
  alRevocar,
}: {
  credencial: Credencial;
  unica: boolean;
  alRevocar: () => void;
}) {
  const revocada = !!c.revocada_en;

  return (
    <li>
      <Tarjeta className={cx("flex flex-wrap items-center gap-x-4 gap-y-2 p-4", revocada && "opacity-60")}>
        <span
          className={cx(
            "grid size-9 shrink-0 place-items-center rounded-xl",
            revocada ? "bg-superficie-2 text-tenue-2" : "bg-marca-suave text-marca-tinta",
          )}
        >
          <KeyRound className="size-4.5" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium text-tinta">{c.nombre || "(sin nombre)"}</span>
            <span className="cifra rounded border border-borde bg-superficie-2 px-1.5 py-px text-[11px] text-tenue">
              {c.prefijo}
            </span>
            {revocada && <Insignia tono="neutro">revocada</Insignia>}
            {unica && <Insignia tono="dato">la única activa</Insignia>}
          </p>
          <p className="mt-0.5 text-xs text-tenue-2">
            {/* El último uso es el dato que hace útil esta lista: una credencial
                viva y sin usar durante meses es la que sobra, y hasta ahora no
                había forma de verlo. */}
            {revocada
              ? `Revocada ${haceRato(c.revocada_en)}`
              : c.ultimo_uso
                ? `Se usó ${haceRato(c.ultimo_uso)}`
                : "Nunca se ha usado"}
            {" · "}
            <span title={fechaHora(c.creada_en)}>
              creada {haceRato(c.creada_en)}
              {c.creada_por ? ` por ${c.creada_por}` : ""}
            </span>
          </p>
        </div>

        {!revocada && (
          <Boton variante="fantasma" tamano="sm" icono={ShieldOff} onClick={alRevocar}>
            Revocar
          </Boton>
        )}
      </Tarjeta>
    </li>
  );
}

/**
 * El token, una sola vez.
 *
 * El diálogo no se cierra al pulsar fuera ni con un aspa discreta: hay que decir
 * que ya se guardó. Es la única pantalla del producto donde cerrar sin leer
 * cuesta tener que empezar de nuevo, y conviene que se note.
 */
function TokenNuevo({
  credencial,
  alCerrar,
}: {
  credencial: CredencialCreada | null;
  alCerrar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    if (!credencial) return;
    try {
      await navigator.clipboard.writeText(credencial.token);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sin permiso de portapapeles todavía se puede seleccionar a mano.
      setCopiado(false);
    }
  }

  return (
    <Dialogo
      abierto={!!credencial}
      alCerrar={alCerrar}
      titulo="Guarda este token ahora"
      descripcion="Es la única vez que se muestra. De él solo guardamos una huella, así que ni nosotros podemos recuperarlo."
      ancho="max-w-2xl"
      pie={
        <Boton variante="primario" icono={Check} onClick={alCerrar}>
          Ya lo guardé
        </Boton>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold tracking-wider text-tenue-2 uppercase">
            {credencial?.nombre}
          </p>
          <div className="flex items-start gap-2">
            <code className="cifra min-w-0 flex-1 overflow-x-auto rounded-xl border border-borde bg-superficie-2 p-3 text-xs break-all text-tinta">
              {credencial?.token}
            </code>
            <Boton variante="suave" icono={copiado ? Check : Copy} onClick={() => void copiar()}>
              {copiado ? "Copiado" : "Copiar"}
            </Boton>
          </div>
        </div>

        <Aviso tono="riesgo" titulo="Trátalo como una contraseña" icono={TriangleAlert}>
          Quien lo tenga puede cargar cartera y leer los pagos de esta cuenta. No lo mandes por
          chat ni lo dejes en un archivo compartido; ponlo donde tu sistema lea sus secretos. Si se
          pierde, revócalo aquí y crea otro.
        </Aviso>

        <p className="text-sm text-tenue">
          En cada petición van dos cabeceras — la segunda se olvida a menudo, y sin ella todo lo que
          no sea una lectura responde 400:
        </p>
        <pre className="cifra overflow-x-auto rounded-xl border border-borde bg-superficie-2 p-3 text-xs whitespace-pre text-tinta">
          {`Authorization: Bearer ${credencial?.token.slice(0, 24) ?? ""}…\nX-Recauda-Cliente: app`}
        </pre>
      </div>
    </Dialogo>
  );
}
